import React from 'react';

import { usePermissionModal } from '$/components/Permission';
import Form from '$/components/ui/Form';
import Text from '$/components/ui/Text';

import { BillingCycle } from '$/GraphQL/schema/schema';
import { MeFragment, PriceTierFragment, SubscriptionSubstatus } from '$/GraphQL/schema/types';

import { KeyIcon } from '$/images/FontAwesomeIcons';

import { useMe } from '$/hooks/useMe';

import { rolesPermissionsMatrix } from '$/shared/frontend/rolesPermissionsMatrix';
import EDate from '$/shared/frontend/utils/EDate';
import { hasPermission } from '$/utils/hasPermission';
import { logger } from '$/utils/logger';

import { ProductInfo } from '../../ProductInfo';
import { AddOnProvider, UpdateAddOnType, WithAddOnProps, useAddOnsContext } from '../AddOnsContext';
import { getCardComponent } from '../CardComponent';
import { Per } from '../Components';
import { getListComponent } from '../ListComponent';
import { UpdateAddOnModal } from '../UpdateAddOnModal';

export class AddOn extends ProductInfo {
  public get description(): string {
    return '';
  }

  public get currentPriceTier(): PriceTierFragment | undefined {
    return this.priceTiers[0];
  }

  public get shortDescription(): string {
    return '';
  }

  public get learnMoreHref(): string {
    return 'https://ewebinar.com/help/subscription-add-ons';
  }

  public get disclosure(): React.ReactElement | null {
    return null;
  }

  public get ctaText(): string | null {
    return `Subscribe to ${this.name}`;
  }

  public getFreeUntil(me: MeFragment): EDate | undefined {
    let freeUntil: EDate | undefined = undefined;
    if (
      [SubscriptionSubstatus.Trialing, SubscriptionSubstatus.TrialingCanceled].includes(
        me.team.subscriptionSubstatus
      ) &&
      me.team.trialEndingDate
    ) {
      freeUntil = new EDate(me.team.trialEndingDate, 'UTC');
    }
    return freeUntil && freeUntil.getTime() > Date.now() ? freeUntil : undefined;
  }

  public getModalTitle(type: UpdateAddOnType): string {
    switch (type) {
      case UpdateAddOnType.Subscribe:
        if (this.isTrialAvailable) {
          return `Subscribe to free trial of add-on?`;
        }
        return `Subscribe to add-on?`;
      case UpdateAddOnType.Resubscribe:
        return `Resubscribe to add-on?`;
      case UpdateAddOnType.Unsubscribe:
        return `Unsubscribe from add-on?`;
    }
  }

  public isDisabledModalSubmit(
    _type: UpdateAddOnType,
    _totalPublished: number
  ): { buttonText: string } | undefined {
    return undefined;
  }

  public getModalMessage(
    type: UpdateAddOnType,
    billingCycle: BillingCycle,
    _totalPublished: number,
    freeUntil?: EDate
  ): React.ReactElement {
    const formattedPrice = this.getFormattedPrice(billingCycle);
    const billingCycleText = billingCycle === BillingCycle.Month ? 'monthly' : 'annual';
    switch (type) {
      case UpdateAddOnType.Subscribe:
        return (
          <Text.body>
            {freeUntil ? (
              <p>
                If you subscribe to the <b>{this.name} add-on</b>, your {billingCycleText}{' '}
                subscription rate will not increase right away. Instead, the add-on will be free for
                you until <b>{freeUntil.format('P')}</b>, at which time you will be charged{' '}
                <b>
                  {formattedPrice}
                  <Per billingCycle={billingCycle} />
                </b>
                .
              </p>
            ) : (
              <p>
                If you subscribe to the <b>{this.name} add-on</b>, your {billingCycleText}{' '}
                subscription rate will increase by{' '}
                <b>
                  {formattedPrice}
                  <Per billingCycle={billingCycle} />
                </b>{' '}
                and you will be charged immediately at a prorated rate for the remainder of this
                billing cycle.
              </p>
            )}

            <p className='font-semibold'>
              Once charged, add-ons are non-refundable for the billing cycle in question. You may
              unsubscribe from them at any time.
            </p>

            {this.disclosure}

            <p>Do you wish to continue?</p>
          </Text.body>
        );
      case UpdateAddOnType.Resubscribe:
        return <div>Update ${this.name} Add-on</div>;
      case UpdateAddOnType.Unsubscribe:
        return (
          <Text.body>
            <p>
              If you unsubscribe from the <b>{this.name} add-on</b>, your subscription to it will
              remain active until the end of this billing cycle. If you subscribe again before that
              time, your access to this feature will remain uninterrupted.
            </p>

            {freeUntil ? (
              <p>
                Please note if you wish to remain subscribed to this add-on, it will remain free for
                you until <b>{freeUntil.format('P')}</b>, at which time you will be charged{' '}
                <b>
                  {formattedPrice}
                  <Per billingCycle={billingCycle} />
                </b>
                .
              </p>
            ) : null}

            <p>Do you wish to continue?</p>
          </Text.body>
        );
    }
  }

  public getCtaButtonText(type: UpdateAddOnType): string {
    switch (type) {
      case UpdateAddOnType.Subscribe:
        if (this.isTrialAvailable) {
          return `Subscribe to ${this.trialDays}-day free trial`;
        }
        return `Subscribe to add-on`;
      case UpdateAddOnType.Resubscribe:
        return `Resubscribe to add-on`;
      case UpdateAddOnType.Unsubscribe:
        return `Unsubscribe from add-on`;
    }
  }

  public get CardComponent(): React.FC {
    return getCardComponent(this);
  }

  public get ListComponent(): React.FC {
    return getListComponent(this);
  }

  public get Icon(): React.FC<{ className?: string }> {
    return KeyIcon;
  }

  public image(): string | null {
    return null;
  }

  public get WithAddOnComponent(): React.FC<WithAddOnProps> {
    const WithAddOn: React.FC<WithAddOnProps> = ({ children, onAddSuccess }) => {
      const me = useMe();
      const { openModal, isModalOpen } = useAddOnsContext();
      const { showPermissionModal } = usePermissionModal();

      const onAdd = React.useCallback(() => {
        if (!this.priceTier) {
          logger.error('Missing priceTier in add-on ', this.name);
          return;
        }
        if (!hasPermission(rolesPermissionsMatrix.accountSubscription.path, me)) {
          showPermissionModal?.(rolesPermissionsMatrix.accountSubscription.path);
          return;
        }
        openModal({
          addOn: this,
          tierId: this.priceTier.tierId,
          type: UpdateAddOnType.Subscribe,
          onAddSuccess,
        });
      }, [me, onAddSuccess, openModal, showPermissionModal]);

      const isAvailable = this.isSubscribed;

      const ManageLink = React.useMemo(() => {
        return (
          <>
            {isAvailable && (
              <Form.Link
                to={rolesPermissionsMatrix.accountSubscription.path}
                className='whitespace-nowrap text-base font-body'
              >
                Manage subscription add-ons
              </Form.Link>
            )}
          </>
        );
      }, [isAvailable]);

      return React.useMemo(
        () => (
          <>
            {children({ isAvailable, onAdd, isModalOpen, ManageLink })}
            <UpdateAddOnModal />
          </>
        ),
        [children, isAvailable, onAdd, ManageLink, isModalOpen]
      );
    };
    const WithAddOnComponent: React.FC<WithAddOnProps> = (props) => {
      return (
        <AddOnProvider>
          <WithAddOn {...props} />
        </AddOnProvider>
      );
    };
    return WithAddOnComponent;
  }
}
